import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronDown, Copy, Check } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { FAQItem as FAQItemType, FAQCategory } from '@/data/types';

interface FAQItemProps {
  item: FAQItemType;
  isExpanded: boolean;
  onToggle: () => void;
  highlightText?: string;
}

// Category display configuration
const categoryConfig: Record<FAQCategory, { label: string; variant: 'lightPrimary' | 'lightSecondary' | 'lightSuccess' | 'lightError' | 'lightWarning' | 'muted' }> = {
  eligibility: { label: 'Eligibility', variant: 'lightSuccess' },
  thresholds: { label: 'Thresholds', variant: 'lightPrimary' },
  measures: { label: 'Measures', variant: 'lightSecondary' },
  exceptions: { label: 'Exceptions', variant: 'lightWarning' },
  process: { label: 'Process', variant: 'muted' },
  communication: { label: 'Communication', variant: 'lightSuccess' },
  general: { label: 'General', variant: 'muted' },
};

function linkifyEmails(text: string): React.ReactNode {
  const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
  const parts = text.split(emailRegex);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    emailRegex.test(part) ? (
      <a key={i} href={`mailto:${part}`} className="text-eventus-purple underline hover:text-eventus-navy">
        {part}
      </a>
    ) : (
      part
    )
  );
}

function highlightMatch(text: string, searchTerm: string): React.ReactNode {
  if (!searchTerm) return linkifyEmails(text);

  const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === searchTerm.toLowerCase() ? (
      <mark key={i} className="bg-eventus-teal/40 text-eventus-navy rounded px-0.5">
        {part}
      </mark>
    ) : (
      linkifyEmails(part)
    )
  );
}

export function FAQItem({ item, isExpanded, onToggle, highlightText }: FAQItemProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const text = `Q: ${item.question}\n\nA: ${item.response}\n\nKey Points:\n${item.keyPoints.map((p) => `- ${p}`).join('\n')}`;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const categoryInfo = categoryConfig[item.category];

  return (
    <Card
      id={item.id}
      className={`transition-all duration-300 ${
        isExpanded
          ? 'ring-2 ring-eventus-purple/30 shadow-lg bg-white'
          : 'hover:shadow-md hover:border-eventus-purple/20'
      }`}
    >
      {/* Question header (clickable) */}
      <button
        className="w-full p-6 text-left flex items-start justify-between gap-4 group"
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-controls={`faq-content-${item.id}`}
      >
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={categoryInfo.variant} className="text-xs">
              {categoryInfo.label}
            </Badge>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 group-hover:text-eventus-purple transition-colors leading-relaxed">
            "{highlightMatch(item.question, highlightText || '')}"
          </h3>
        </div>
        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0 mt-2"
        >
          <ChevronDown className="w-5 h-5 text-gray-400 group-hover:text-eventus-purple transition-colors" />
        </motion.div>
      </button>

      {/* Expandable content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            id={`faq-content-${item.id}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <CardContent className="pt-0 pb-6 px-6 border-t border-gray-100">
              {/* Recommended Response */}
              <div className="mb-6 mt-4">
                <h4 className="text-base font-semibold text-eventus-purple mb-3 flex items-center gap-2">
                  <span className="w-1 h-5 bg-eventus-purple rounded-full" />
                  Recommended Response
                </h4>
                <div className="bg-gradient-to-br from-gray-50 to-eventus-purple/5 p-5 rounded-xl border border-gray-100">
                  <p className="text-gray-700 leading-relaxed italic text-lg">
                    "{highlightMatch(item.response, highlightText || '')}"
                  </p>
                </div>
              </div>

              {/* Key Points */}
              <div className="mb-6">
                <h4 className="text-base font-semibold text-eventus-navy mb-3 flex items-center gap-2">
                  <span className="w-1 h-5 bg-eventus-navy rounded-full" />
                  Key Points to Reinforce
                </h4>
                <ul className="space-y-3">
                  {item.keyPoints.map((point, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="flex items-start gap-3 text-gray-600"
                    >
                      <span className="w-2 h-2 rounded-full bg-eventus-teal mt-2.5 flex-shrink-0" />
                      <span className="leading-relaxed text-lg">{highlightMatch(point, highlightText || '')}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>

              {/* Action buttons */}
              <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100">
                <Button
                  variant="outline"
                  size="default"
                  onClick={handleCopy}
                  className="flex items-center gap-2 border-eventus-purple/30 text-eventus-purple hover:bg-eventus-purple/10"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy Response
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}
